import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { fmtDate, fmtIDR } from "@/lib/api";
import { PageHeader, Loading, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ensureStoreLocation } from "@/lib/geofence";
import BarcodeScanner from "@/components/BarcodeScanner";
import { ClipboardCheck, Boxes, Package, AlertTriangle } from "lucide-react";

const Card = ({ icon: Icon, label, value, tone = "bg-slate-100 text-slate-600", grad, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 16, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay, duration: 0.5 }} whileHover={{ y: -6 }} className={`rounded-xl border shadow-sm p-4 ${grad ? "text-white border-transparent " + grad : "bg-white border-slate-200"}`} data-testid={`stock-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
    <div className={`flex items-center gap-2 text-xs uppercase tracking-wide font-medium ${grad ? "opacity-90" : "text-slate-500"}`}>
      {!grad && <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${tone}`}><Icon className="w-4 h-4" /></span>}
      {grad && <Icon className="w-4 h-4" />} {label}
    </div>
    <div className={`text-xl font-semibold font-mono-num mt-2 ${grad ? "" : "text-slate-900"}`}>{value}</div>
  </motion.div>
);

export default function Stock() {
  const [moves, setMoves] = useState([]);
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [unitRows, setUnitRows] = useState([]);
  const [unitScanOpen, setUnitScanOpen] = useState(false);
  const [savingOpname, setSavingOpname] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/stock/movements", { params: { q: q || undefined, start: start || undefined, end: end || undefined, category: category || undefined, page, limit: 200 } })
      .then(({ data }) => setMoves(data.items || data)).finally(() => setLoading(false));
    api.get("/stock/summary").then(({ data }) => setSummary(data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/products").then(({ data }) => setProducts(data.items || data)).catch(() => setProducts([]));
    const timer = window.setInterval(load, 30000);
    return () => window.clearInterval(timer);
  }, [q, start, end, category, page]);

  const openOpname = async () => {
    try {
      const { data } = await api.get("/stock/opname/units");
      setUnitRows((data || []).map((unit) => ({ ...unit, physical_status: "ada" })));
      setOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat unit IMEI"); }
  };

  const markUnitByScan = (code) => {
    setUnitScanOpen(false);
    const index = unitRows.findIndex((unit) => unit.imei === code);
    if (index < 0) return toast.error(`IMEI ${code} tidak ada di daftar opname`);
    setUnitRows((rows) => rows.map((unit, rowIndex) => rowIndex === index ? { ...unit, physical_status: "ada" } : unit));
    toast.success(`IMEI ${code} ditandai Ada`);
  };

  const saveOpname = async () => {
    if (unitRows.length === 0) return toast.error("Tidak ada unit untuk opname");
    setSavingOpname(true);
    try {
      const prevScroll = typeof window !== "undefined" ? window.scrollY : 0;
      await ensureStoreLocation();
      await api.post("/stock/opname/units", { note: "Opname per IMEI", units: unitRows });
      toast.success("Opname IMEI tersimpan"); setOpen(false); setUnitRows([]); load();
      if (typeof window !== "undefined") setTimeout(() => window.scrollTo({ top: prevScroll }), 50);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Gagal menyimpan opname");
    } finally { setSavingOpname(false); }
  };

  const KIND = { sale: "Penjualan", purchase: "Pembelian", opname: "Opname", trade_in: "Tukar Tambah" };
  const s = summary || {};

  return (
    <div>
      <PageHeader title="Stok & Opname" subtitle="Nilai stok, mutasi & stock opname">
        <Button onClick={openOpname} className="bg-sky-600 hover:bg-sky-700" data-testid="opname-btn"><ClipboardCheck className="w-4 h-4 mr-1.5" /> Stock Opname</Button>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl p-5 text-white bg-gradient-to-br from-slate-800 to-sky-900"><h3 className="font-semibold mb-3">Unit HP</h3><div className="grid grid-cols-3 gap-3 text-sm"><div><span className="block text-white/60 text-xs">Total Unit</span><b className="font-mono-num">{s.categories?.hp?.units || 0}</b></div><div><span className="block text-white/60 text-xs">Nilai Modal</span><b className="font-mono-num">{fmtIDR(s.categories?.hp?.modal)}</b></div><div><span className="block text-white/60 text-xs">Potensi Laba</span><b className="font-mono-num">{fmtIDR(s.categories?.hp?.potential_profit)}</b></div></div></motion.div>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="rounded-2xl p-5 text-white bg-gradient-to-br from-violet-700 to-fuchsia-800"><h3 className="font-semibold mb-3">Aksesoris / Lainnya</h3><div className="grid grid-cols-3 gap-3 text-sm"><div><span className="block text-white/60 text-xs">Total Unit</span><b className="font-mono-num">{s.categories?.aksesoris?.units || 0}</b></div><div><span className="block text-white/60 text-xs">Nilai Modal</span><b className="font-mono-num">{fmtIDR(s.categories?.aksesoris?.modal)}</b></div><div><span className="block text-white/60 text-xs">Potensi Laba</span><b className="font-mono-num">{fmtIDR(s.categories?.aksesoris?.potential_profit)}</b></div></div></motion.div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Card icon={Package} label="Total SKU" value={s.total_products ?? 0} tone="bg-slate-100 text-slate-600" />
        <Card icon={AlertTriangle} label="Stok Menipis" value={s.low_stock_count ?? 0} tone="bg-amber-50 text-amber-600" />
        <Card icon={AlertTriangle} label="Stok Habis" value={s.out_of_stock ?? 0} tone="bg-red-50 text-red-600" />
        <Card icon={Boxes} label="Total Unit" value={s.total_units ?? 0} tone="bg-sky-50 text-sky-600" />
      </div>

      <h3 className="font-medium text-slate-800 mb-3">Mutasi Stok Terakhir</h3>
      <div className="mb-3 flex flex-wrap gap-2">
        <Input placeholder="Cari produk / referensi" value={q} onChange={(e) => setQ(e.target.value)} />
        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="border border-slate-200 rounded-md px-3 text-sm bg-white"><option value="">Semua Kategori</option><option value="hp">Unit HP</option><option value="aksesoris">Aksesoris / Lainnya</option></select>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            {["Tanggal", "Produk", "Jenis", "Perubahan", "Referensi"].map((h) => <th key={h} className="px-4 py-3 text-left font-medium text-slate-500">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td colSpan={5}><Loading /></td></tr>
              : moves.length === 0 ? <tr><td colSpan={5}><Empty /></td></tr>
              : moves.map((m) => (
                <tr key={m.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500">{fmtDate(m.date)}</td>
                  <td className="px-4 py-3">{m.product_name}</td>
                  <td className="px-4 py-3">{KIND[m.kind] || m.kind}</td>
                  <td className={`px-4 py-3 font-mono-num font-medium ${m.delta >= 0 ? "text-green-600" : "text-red-600"}`}>{m.delta > 0 ? "+" : ""}{m.delta}</td>
                  <td className="px-4 py-3 font-mono-num text-xs text-slate-400">{m.ref}</td>
                </tr>
              ))}
          </tbody>
        </table>
        <div className="p-3 flex items-center justify-between">
          <div className="text-sm text-slate-500">Halaman {page}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
            <Button size="sm" onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Stock Opname</DialogTitle></DialogHeader>
          <div className="flex items-center justify-between mb-2"><p className="text-xs text-slate-500">Tandai unit yang ditemukan secara fisik. Unit yang belum ditemukan akan berwarna merah.</p><Button type="button" variant="outline" onClick={() => setUnitScanOpen(true)}>Scan IMEI</Button></div>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0"><tr>
                {["Produk", "IMEI", "Status Sistem", "Fisik", "Verifikasi"].map((h) => <th key={h} className="px-3 py-2 text-left text-slate-500 font-medium">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {unitRows.map((unit) => {
                  const found = unit.physical_status === "ada";
                  return <tr key={unit.unit_id} className={found ? "bg-emerald-50/80" : "bg-red-50/80"}>
                    <td className="px-3 py-2 font-medium">{unit.product_name}</td>
                    <td className="px-3 py-2 font-mono-num text-xs">{unit.imei}</td>
                    <td className="px-3 py-2 text-xs">{unit.system_status}</td>
                    <td className="px-3 py-2"><select value={unit.physical_status} onChange={(e) => setUnitRows((rows) => rows.map((row) => row.unit_id === unit.unit_id ? { ...row, physical_status: e.target.value } : row))} className="border rounded px-2 py-1 text-sm"><option value="ada">Ada</option><option value="tidak_ada">Tidak Ada</option></select></td>
                    <td className={`px-3 py-2 font-semibold ${found ? "text-emerald-700" : "text-red-700"}`}>{found ? "Ditemukan / Ada" : "Belum Ditemukan"}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={saveOpname} disabled={savingOpname} className="bg-sky-600 hover:bg-sky-700" data-testid="opname-save-btn">{savingOpname ? "Menyimpan..." : "Simpan Opname"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <BarcodeScanner open={unitScanOpen} onClose={() => setUnitScanOpen(false)} onScan={markUnitByScan} />
    </div>
  );
}
